/**
 * @fileoverview Main application entry point for the KeyReply Dashboard.
 *
 * This module initializes the Vue application with all required dependencies:
 * - Core-js polyfills for browser compatibility
 * - Vue Router for navigation
 * - Vuex Store for state management
 * - Web Worker for background processing
 * - Custom directives and device detection
 *
 * @module main
 */

// Polyfill all `core-js` features for browser compatibility
import "core-js";
import { Vue, worker } from "./bootstrap";
import Root from "@/Root.vue";
import router from "@/router";
import store from "@/store";

/**
 * Custom Vue directive that automatically focuses the first input element
 * within an Element UI component when inserted into the DOM.
 *
 * @directive v-focus
 * @example
 * <el-input v-focus placeholder="This will be focused on mount" />
 */
Vue.directive("focus", {
  inserted: function (el) {
    el.querySelector("input")?.focus();
  },
});

/**
 * Device detection plugin that adds `$device` property to all Vue components.
 * Detects platform, browser, and device type from user agent string.
 *
 * @example
 * // In any component
 * if (this.$device.Mobile) {
 *   // Show mobile-optimized UI
 * }
 * if (this.$device.ie) {
 *   // Apply IE-specific fixes
 * }
 */
Vue.use({
  /**
   * Installs the device detection plugin.
   *
   * @param {VueConstructor} V - The Vue constructor
   */
  install(V) {
    const v = V;
    const ua = navigator.userAgent;
    const params = new URLSearchParams(location.search);

    /** @type {boolean} Whether mode=mobile query param is present */
    const isMobileQuery = params.get("mode") === "mobile";
    /** @type {boolean} Whether device is iPad or iPhone */
    const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    /** @type {boolean} Whether running in KeyReply iOS SDK webview */
    const iOSSDK = !!ua.match(/KeyReplyiOSSDK/i);
    /** @type {boolean} Whether device is Android */
    const Android = !!ua.match(/Android/i);
    /** @type {boolean} Whether device is mobile (any platform) */
    const Mobile = !!ua.match(/Mobi/i);
    /** @type {boolean} Whether device is Macintosh */
    const Mac = !!ua.match(/Macintosh/i);

    /**
     * Detects Internet Explorer or legacy Edge browser.
     *
     * @returns {boolean} True if browser is IE 9/10/11 or legacy Edge
     */
    const isIEOrEdge = () => {
      if (!navigator) return false;
      let _ieOrEdge = false;
      if (/MSIE 10/i.test(navigator.userAgent)) {
        _ieOrEdge = true;
      }
      if (/MSIE 9/i.test(navigator.userAgent) || /rv:11.0/i.test(navigator.userAgent)) {
        _ieOrEdge = true;
      }
      if (/Edge\/\d./i.test(navigator.userAgent)) {
        _ieOrEdge = true;
      }
      return _ieOrEdge;
    };

    /**
     * Device detection object available as this.$device in components.
     *
     * @typedef {Object} DeviceInfo
     * @property {boolean} iOS - True if iPad or iPhone
     * @property {boolean} iOSSDK - True if KeyReply iOS SDK
     * @property {boolean} Android - True if Android device
     * @property {boolean} Mobile - True if any mobile device
     * @property {boolean} Mac - True if Macintosh
     * @property {boolean} isMobileQuery - True if ?mode=mobile in URL
     * @property {boolean} ie - True if IE or legacy Edge
     */
    v.prototype.$device = {
      iOS,
      iOSSDK,
      Android,
      Mobile,
      Mac,
      isMobileQuery,
      ie: isIEOrEdge(),
    };
  },
});

/**
 * Initializes and mounts the Vue application.
 *
 * Creates the root Vue instance with router and store, mounts it to the #app element,
 * and optionally exposes the instance to the window.$keyreply object for external access.
 *
 * @returns {Vue} The mounted Vue instance
 *
 * @example
 * // External access (if $keyreply is defined)
 * window.$keyreply.getVM().$store.state.profile
 */
function main() {
  const vm = new Vue({
    router,
    store,
    render: (h) => h(Root as any),
  }).$mount("#app");

  // Expose VM to external KeyReply integration if available
  const $keyreply = (window as any).$keyreply;
  if ($keyreply) {
    $keyreply.getVM = () => vm;
  }
  return vm;
}

/**
 * Web Worker message handler.
 * Processes messages from the background worker and commits them as Vuex mutations.
 * This enables offloading heavy computations to a worker thread while keeping
 * the store in sync.
 *
 * @param {MessageEvent} event - The message event from the worker
 * @param {Object} event.data - The message data
 * @param {string} event.data.type - The Vuex mutation type to commit
 * @param {*} event.data.payload - The mutation payload
 */
worker.onmessage = ({ data }) => {
  const { type, payload } = data;
  if (type) {
    store.commit(type, payload);
  }
};

// Bootstrap the application
main();
